"use client";

/**
 * The call being reviewed, docked to the bottom of the screen while a recording
 * is loaded (see CallRecordingContext). It survives tab and page changes, so an
 * agent can listen while reading the lead file. Transport on the left, which
 * call it is in the middle, transcript + way back to the call on the right.
 */

import Link from "next/link";
import { format } from "date-fns";
import { AlertCircle, ArrowUpRight, FileText, Loader2, Pause, PhoneIncoming, PhoneOutgoing, Play, X } from "lucide-react";
import React, { useState } from "react";

import { cn } from "@/app/_module/lib/utils";
import { fmtClock } from "@/app/_module/components/RecordingPlayer";
import { PLAYBACK_RATES, useCallRecording, useCallRecordingClock } from "@/app/_module/contexts/CallRecordingContext";
import { useEnrollmentFloater } from "@/app/_module/contexts/EnrollmentFloaterContext";
import { focusRing } from "@/app/(authenticated)/settings/a2p-verification/_components/overview/primitives";
import { CallTranscriptPanel } from "@/app/(authenticated)/leads/_modules/components/CallTranscriptPanel";

const callWhen = (d?: string | Date | null) => {
  if (!d) return "";
  const date = d instanceof Date ? d : new Date(d);
  return Number.isNaN(date.getTime()) ? "" : format(date, "MMM d, h:mm a");
};

export function CallRecordingBar({
  leadHref,
  callsHref,
  className,
}: {
  /** Where "Open lead" goes for this shell (desktop lead page vs /m). */
  leadHref: (leadId: string) => string;
  /** Fallback when the call has no lead (an unknown caller). */
  callsHref: string;
  /** Bottom offset; the mobile shell sits above its dock. */
  className?: string;
}) {
  const player = useCallRecording();
  const { time, duration } = useCallRecordingClock();
  const { popupOpen } = useEnrollmentFloater();
  const [showTranscript, setShowTranscript] = useState(false);

  const rec = player?.current;
  if (!player || !rec) return null;

  const { status, rate } = player;
  const playing = status === "playing";
  const total = duration || Number(rec.durationSecs) || 0;
  const inbound = rec.direction === "inbound";
  const DirectionIcon = inbound ? PhoneIncoming : PhoneOutgoing;
  const when = callWhen(rec.callDate);
  const nextRate = PLAYBACK_RATES[(PLAYBACK_RATES.indexOf(rate as (typeof PLAYBACK_RATES)[number]) + 1) % PLAYBACK_RATES.length];
  const href = rec.leadId ? leadHref(rec.leadId) : callsHref;

  return (
    <div
      className={cn(
        "pointer-events-none fixed left-[env(safe-area-inset-left,0px)] right-[env(safe-area-inset-right,0px)] z-[45] flex justify-center px-3",
        // The enrollment popup pill also sits bottom-center.
        popupOpen ? "bottom-20" : "bottom-3",
        className,
      )}
    >
      <div className="pointer-events-auto w-full max-w-3xl animate-in fade-in slide-in-from-bottom-2 duration-200 motion-reduce:animate-none">
        {showTranscript && (
          <div className="mb-2 max-h-[45vh] overflow-y-auto rounded-2xl border bg-background p-2 shadow-xl">
            <CallTranscriptPanel recording={rec} className="mt-0 border-0" />
          </div>
        )}

        {/* Phones: play · who · actions, then a full-width scrubber. sm+: one row, scrubber under who. */}
        <div
          role="region"
          aria-label={`Call recording: ${rec.title}`}
          className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-2 gap-y-1.5 rounded-2xl border bg-background px-2.5 py-2 shadow-xl sm:gap-x-3 sm:px-3"
        >
          <div className="col-start-1 row-start-1 flex items-center gap-1 sm:row-span-2">
            <button
              type="button"
              onClick={() => player.skip(-10)}
              disabled={status !== "playing" && status !== "paused"}
              aria-label="Back 10 seconds"
              className={cn(
                "hidden h-8 items-center rounded-full px-2 text-xs font-medium tabular-nums text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-40 sm:inline-flex",
                focusRing,
              )}
            >
              −10s
            </button>
            <button
              type="button"
              onClick={player.toggle}
              disabled={status === "loading"}
              aria-label={status === "error" ? "Try again" : playing ? "Pause" : "Play"}
              className={cn(
                "flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm transition-opacity hover:opacity-90 disabled:opacity-70",
                focusRing,
              )}
            >
              {status === "loading" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : playing ? (
                <Pause className="h-4 w-4" />
              ) : (
                <Play className="ml-0.5 h-4 w-4" />
              )}
            </button>
            <button
              type="button"
              onClick={() => player.skip(10)}
              disabled={status !== "playing" && status !== "paused"}
              aria-label="Forward 10 seconds"
              className={cn(
                "hidden h-8 items-center rounded-full px-2 text-xs font-medium tabular-nums text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-40 sm:inline-flex",
                focusRing,
              )}
            >
              +10s
            </button>
          </div>

          <div className="col-start-2 row-start-1 flex min-w-0 items-center gap-1.5 text-sm">
            <DirectionIcon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
            <span className="sr-only">{inbound ? "Inbound call with" : "Outbound call to"}</span>
            <span className="truncate font-medium text-foreground">{rec.title}</span>
            {status === "error" ? (
              <span className="flex shrink-0 items-center gap-1 text-xs text-red-600">
                <AlertCircle className="h-3.5 w-3.5" aria-hidden /> Couldn&apos;t load the recording
              </span>
            ) : (
              when && <span className="hidden shrink-0 text-xs text-muted-foreground sm:inline">· {when}</span>
            )}
          </div>
          <div className="col-span-3 row-start-2 flex items-center gap-2 sm:col-span-1 sm:col-start-2">
            <span className="w-9 shrink-0 text-right text-[11px] tabular-nums text-muted-foreground">{fmtClock(time)}</span>
            <input
              type="range"
              min={0}
              max={total || 0}
              step={0.1}
              value={Math.min(time, total || 0)}
              disabled={!total || status === "loading" || status === "error"}
              onChange={(e) => player.seek(Number(e.target.value))}
              aria-label="Position in recording"
              aria-valuetext={`${fmtClock(time)} of ${fmtClock(total)}`}
              className={cn("h-1.5 min-w-0 flex-1 cursor-pointer accent-primary disabled:cursor-default", focusRing)}
            />
            <span className="w-9 shrink-0 text-[11px] tabular-nums text-muted-foreground">{fmtClock(total)}</span>
          </div>

          <div className="col-start-3 row-start-1 flex items-center gap-0.5 sm:row-span-2 sm:gap-1">
            <button
              type="button"
              onClick={() => player.setRate(nextRate)}
              aria-label={`Playback speed ${rate}x. Change to ${nextRate}x`}
              className={cn(
                "rounded-full px-2 py-1 text-xs font-semibold tabular-nums transition-colors",
                rate !== 1 ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                focusRing,
              )}
            >
              {rate}x
            </button>
            <button
              type="button"
              onClick={() => setShowTranscript((v) => !v)}
              aria-pressed={showTranscript}
              aria-label="Transcript"
              className={cn(
                "inline-flex h-8 items-center gap-1.5 rounded-full px-2 text-xs font-medium transition-colors",
                showTranscript ? "bg-primary/10 text-primary" : "text-muted-foreground hover:bg-muted hover:text-foreground",
                focusRing,
              )}
            >
              <FileText className="h-4 w-4" aria-hidden />
              <span className="hidden md:inline">Transcript</span>
            </button>
            <Link
              href={href}
              aria-label={rec.leadId ? "Open lead" : "Open calls"}
              className={cn(
                "inline-flex h-8 items-center gap-1 rounded-full px-2 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
                focusRing,
              )}
            >
              <span className="hidden md:inline">{rec.leadId ? "Open lead" : "Open calls"}</span>
              <ArrowUpRight className="h-4 w-4" aria-hidden />
            </Link>
            <button
              type="button"
              onClick={() => {
                setShowTranscript(false);
                player.close();
              }}
              aria-label="Close recording"
              className={cn(
                "flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
                focusRing,
              )}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
